import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"

import type { IPagination } from "@common-shared/types/general.types"
import type { IOneVerifyPayerMapping } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { UtilHelper } from "../../helpers/util.helper"
import type { IPayerMappingsState } from "../../types/state.types"

const initialState: IPayerMappingsState = {
    items: [],
    itemsPagination: UtilHelper.getPagination(),
    unfilteredItemsTotal: 0,
    exportProgress: 0,
}

const payerMappingsSlice = createSlice({
    name: "payerMappings",
    initialState,
    reducers: {
        payerMappingsReset: () => initialState,

        payerMappingsSetList: (
            state,
            action: PayloadAction<{
                items: IOneVerifyPayerMapping[]
                newPagination: IPagination
                isUnfiltered: boolean
            }>,
        ) => {
            state.items = action.payload.items
            state.itemsPagination = action.payload.newPagination

            // A filtered response only counts the matches, so it must not
            // overwrite the practice-wide total
            if (action.payload.isUnfiltered) {
                state.unfilteredItemsTotal = action.payload.newPagination.total
            }
        },

        // The refetch after a delete carries the table's active search, so a
        // filtered response cannot correct the practice-wide total. An unfiltered
        // one still overwrites this with the authoritative count.
        payerMappingsDeleteDone: state => {
            state.unfilteredItemsTotal = Math.max(
                0,
                state.unfilteredItemsTotal - 1,
            )
        },

        payerMappingsUpdateDone: (
            state,
            action: PayloadAction<IOneVerifyPayerMapping>,
        ) => {
            const newItems = [...state.items]

            const neededItemsIndex = newItems.findIndex(
                item => item.id === action.payload.id,
            )

            if (~neededItemsIndex) {
                newItems[neededItemsIndex] = action.payload
            }

            state.items = newItems
        },

        payerMappingsSetExportProgress: (
            state,
            action: PayloadAction<number>,
        ) => {
            state.exportProgress = action.payload
        },
    },
})

export const {
    payerMappingsReset,
    payerMappingsSetList,
    payerMappingsDeleteDone,
    payerMappingsUpdateDone,
    payerMappingsSetExportProgress,
} = payerMappingsSlice.actions

export const payerMappingsReducer = payerMappingsSlice.reducer
